/**
 * `mailto:` utility functions
 *
 * The mail-client shortcut is the SECONDARY path for sending a compliance message —
 * it can silently fail (no registered mail client gives no callback) and it can be
 * silently TRUNCATED by the OS. Historically the ceiling is ~2000 characters on
 * Windows/Outlook, so callers must gate the button on `isWithinMailtoLimit` rather
 * than fire a URL that will arrive cut in half.
 *
 * Both functions are pure and cheap — the guard is re-evaluated on every keystroke
 * because the draft body is user-editable and can be pasted into.
 */

export interface MailtoParts {
  /** Recipient address */
  to: string;
  /** Subject line — newlines are collapsed, headers are single-line */
  subject: string;
  /** Message body — line breaks are emitted as CRLF (`%0D%0A`) */
  body: string;
}

/**
 * Maximum length of the ENCODED `mailto:` URL we are willing to hand to the OS.
 * Set below the historical ~2000 char truncation point to leave headroom for the
 * client's own handling of the URL.
 */
export const MAILTO_MAX_ENCODED_LENGTH = 1800;

/**
 * Build a `mailto:` URL with every part percent-encoded.
 *
 * Line breaks in the body are normalized to CRLF and therefore encode as `%0D%0A`,
 * which is what mail clients expect; a bare `%0A` is inconsistently honored.
 *
 * Empty parts are omitted rather than emitted as `subject=&body=`.
 *
 * @param parts - Recipient, subject, and body
 * @returns A fully encoded `mailto:` URL
 */
export function buildMailtoUrl({ to, subject, body }: MailtoParts): string {
  const recipient = encodeMailtoRecipient(collapseNewlines(to ?? '').trim());

  const params: string[] = [];
  const encodedSubject = encodeMailtoComponent(collapseNewlines(subject ?? '').trim());
  const encodedBody = encodeMailtoComponent(normalizeNewlines(body ?? ''));

  if (encodedSubject) {
    params.push(`subject=${encodedSubject}`);
  }

  if (encodedBody) {
    params.push(`body=${encodedBody}`);
  }

  return params.length > 0 ? `mailto:${recipient}?${params.join('&')}` : `mailto:${recipient}`;
}

/**
 * Whether an encoded `mailto:` URL is short enough to be safely opened.
 *
 * Over the limit the caller should hide or disable the "Open in Email App" action
 * with a short explanation — a truncated compliance submission is worse than no
 * shortcut at all, and the truncation is silent so there is nothing to detect.
 *
 * @param url - The encoded URL produced by `buildMailtoUrl`
 * @returns `true` when the URL is at or under {@link MAILTO_MAX_ENCODED_LENGTH}
 */
export function isWithinMailtoLimit(url: string): boolean {
  return (url ?? '').length <= MAILTO_MAX_ENCODED_LENGTH;
}

/**
 * Encode the recipient, but leave `@` LITERAL.
 *
 * ⚠️ Do not "simplify" this to `encodeMailtoComponent`. In a `mailto:` URI the address sits in
 * the PATH, where `@` is a legal character (RFC 6068 addr-spec) and must not be escaped.
 * `mailto:user%40example.com` is what `encodeURIComponent` produces, and Windows' protocol
 * handler and most desktop mail clients silently do nothing with it — the click appears to be
 * ignored, with no error to detect. Everything else still gets encoded, so a hostile display
 * name or a stray space can't break out of the URL.
 */
function encodeMailtoRecipient(value: string): string {
  return encodeMailtoComponent(value).replace(/%40/g, '@');
}

/**
 * Percent-encode a URL component for `mailto:`.
 *
 * `encodeURIComponent` leaves `!'()*` untouched; those are additionally escaped so
 * the output is strict RFC 3986 and no client's URL parser can trip on a quote or
 * a parenthesis inside an org name.
 */
function encodeMailtoComponent(value: string): string {
  return encodeURIComponent(value).replace(
    /[!'()*]/g,
    (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`
  );
}

/**
 * Normalize LF and lone CR to CRLF so encoding yields `%0D%0A` exactly once per break.
 */
function normalizeNewlines(value: string): string {
  return value.replace(/\r\n|\r|\n/g, '\r\n');
}

/**
 * Collapse any line break into a single space — headers (`to`, `subject`) are
 * single-line, and a raw break in a header has no valid meaning.
 */
function collapseNewlines(value: string): string {
  return value.replace(/\r\n|\r|\n/g, ' ');
}
